export type ChainBlockedInterval = {
  startMinute: number;
  duration: number;
  reason: string;
};

// Each day is a string: Y, N, a date window, or [{time,duration,message},...].
// The interval notation is produced by the backend and is not JSON. In
// particular, the message can be empty and can contain commas.
export function parseChainBlockedIntervals(info: string): ChainBlockedInterval[] | null {
  if (!info.startsWith('[')) return null;
  if (!info.endsWith(']')) throw new Error('Ungültige Zeitintervalle');
  const intervals: ChainBlockedInterval[] = [];
  let remaining = info.slice(1, -1).trim();
  while (remaining) {
    const match = /^\{\s*(\d+)\s*,\s*(\d+)\s*,([^{}]*)\}/.exec(remaining);
    if (!match) throw new Error('Ungültige Zeitintervalle');
    const startMinute = Number(match[1]), duration = Number(match[2]);
    if (!Number.isSafeInteger(startMinute) || !Number.isSafeInteger(duration) ||
      startMinute < 0 || startMinute >= 1440 || duration <= 0 || startMinute + duration > 1440) {
      throw new Error('Ungültige Zeitintervalle');
    }
    intervals.push({ startMinute, duration, reason: match[3] });
    remaining = remaining.slice(match[0].length).trim();
    if (!remaining) break;
    if (!remaining.startsWith(',') || !remaining.slice(1).trim()) throw new Error('Ungültige Zeitintervalle');
    remaining = remaining.slice(1).trim();
  }
  return intervals;
}

// Touching endpoints are allowed: a treatment may end exactly as a block starts.
export function chainIntervalOverlaps(startMinute: number, duration: number, blocked: ChainBlockedInterval) {
  return startMinute < blocked.startMinute + blocked.duration && startMinute + duration > blocked.startMinute;
}

// A weekly group remains selectable wherever any eligible provider is free.
// Only shade portions blocked for every eligible provider in that column.
export function sharedChainBlockedIntervals(providerIntervals: readonly (readonly ChainBlockedInterval[])[]): ChainBlockedInterval[] {
  if (!providerIntervals.length) return [];
  if (providerIntervals.length === 1) return [...providerIntervals[0]];
  const boundaries = Array.from(new Set(providerIntervals.flatMap((intervals) =>
    intervals.flatMap(({ startMinute, duration }) => [startMinute, startMinute + duration])))).sort((a, b) => a - b);
  const shared: ChainBlockedInterval[] = [];
  boundaries.slice(0, -1).forEach((startMinute, index) => {
    const duration = boundaries[index + 1] - startMinute;
    const overlaps = providerIntervals.map((intervals) => intervals.filter((interval) => chainIntervalOverlaps(startMinute, duration, interval)));
    if (overlaps.every((intervals) => intervals.length > 0)) {
      const reasons = overlaps.flatMap((intervals) => intervals.map((interval) => interval.reason)).filter(Boolean);
      shared.push({ startMinute, duration, reason: Array.from(new Set(reasons)).join('\n') });
    }
  });
  return shared;
}
